import React, { useState, useEffect } from 'react';
import Card from '../../common/Card/Card';
import Button from '../../common/Button/Button';
import Modal from '../../common/Modal/Modal';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import {
  getDurationOverridesAwaitingApproval,
  approveDurationOverride,
  rejectDurationOverride,
} from '../../../services/classService';
import { formatDate, formatDateTime } from '../../../utils/helpers';
import { useToast } from '../../../context/ToastContext';
import styles from './MilestoneApprovalSection.module.css';

const DurationOverrideApproval = () => {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const toast = useToast();

  useEffect(() => {
    fetchOverrides();
  }, []);

  const fetchOverrides = async () => {
    try {
      const data = await getDurationOverridesAwaitingApproval();
      setOverrides(data);
    } catch (error) {
      console.error('Error fetching duration overrides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (override) => {
    setSelected(override);
    setActionType('approve');
    setFeedback('');
    setShowFeedbackModal(true);
  };

  const handleRejectClick = (override) => {
    setSelected(override);
    setActionType('reject');
    setFeedback('');
    setShowFeedbackModal(true);
  };

  const handleSubmitFeedback = async () => {
    if (actionType === 'reject' && !feedback.trim()) {
      toast.warning('Feedback is required when rejecting a duration review');
      return;
    }

    try {
      if (actionType === 'approve') {
        await approveDurationOverride(selected.id, feedback || null);
        toast.success('Duration review approved — attendance duration updated!');
      } else {
        await rejectDurationOverride(selected.id, feedback);
        toast.success('Duration review rejected. Feedback sent to mentor.');
      }

      setShowFeedbackModal(false);
      setFeedback('');
      fetchOverrides();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (overrides.length === 0) {
    return (
      <Card>
        <div className={styles.empty}>
          <p>No duration reviews awaiting approval</p>
        </div>
      </Card>
    );
  }

  return (
    <div className={styles.milestoneApprovals}>
      {overrides.map(override => (
        <Card key={override.id} className={styles.milestoneCard}>
          <div className={styles.cardHeader}>
            <div>
              <h3>Class {override.class_number} — {formatDate(override.class_date)}</h3>
              <p className={styles.projectName}>
                Project: {override.project_name || 'Unknown Project'}
                {override.mentor_name ? ` · Mentor: ${override.mentor_name}` : ''}
              </p>
              <p className={styles.requestInfo}>
                Requested: {formatDateTime(override.duration_override_requested_at)}
              </p>
            </div>
          </div>

          <div className={styles.milestoneItems}>
            <strong>Requested Duration:</strong> {override.manual_duration_minutes} minutes
            <br />
            <strong>Reason:</strong> {override.duration_override_reason}
          </div>

          <div className={styles.actions}>
            <Button size="small"
              style={{ background: '#4CAF50' }}
              onClick={() => handleApproveClick(override)} loading={loading}
            >
              Approve
            </Button>
            <Button size="small"
              variant="danger"
              onClick={() => handleRejectClick(override)} loading={loading}
            >
              Reject
            </Button>
          </div>
        </Card>
      ))}

      <Modal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title={actionType === 'approve' ? 'Approve Duration Review' : 'Reject Duration Review'}
      >
        <div className={styles.feedbackModal}>
          <p className={styles.confirmMessage}>
            Are you sure you want to {actionType} the duration review for <strong>Class {selected?.class_number}</strong>
            {' '}({selected?.manual_duration_minutes} minutes)?
          </p>

          <div className={styles.formGroup}>
            <label>Feedback {actionType === 'reject' ? '(Required)' : '(Optional)'}</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              placeholder={actionType === 'reject' ? "Explain why this request is rejected..." : "Add any comments..."}
              className={styles.textarea}
              required={actionType === 'reject'}
            />
          </div>

          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setShowFeedbackModal(false)} loading={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmitFeedback} loading={loading}
              style={{ background: actionType === 'approve' ? '#4CAF50' : '#F44336' }}
            >
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DurationOverrideApproval;
